import { ApplicationRef, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { Subject, interval, race, timer } from 'rxjs';
import { first, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SystemVersionUpdateDialogComponent } from '../dialogs/system-version-update-dialog/system-version-update-dialog.component';

export function InitialSystemVersionServiceFactory(
    environmentService: SystemVersionService
) {
    return () => environmentService.initialize();
}

@Injectable()
export class SystemVersionService {
    private _dialogRef: MatDialogRef<SystemVersionUpdateDialogComponent>;
    private _onDestroy$ = new Subject<void>();

    constructor(
        private swUpdate: SwUpdate,
        private matDialog: MatDialog,
        private appRef: ApplicationRef
    ) {}

    public initialize() {
        const unrecoverable$ = this.swUpdate.unrecoverable.pipe(
            tap(() => document.location.reload())
        );

        race(
            this.appRef.isStable.pipe(first(isStable => !!isStable)),
            timer(3000).pipe(tap(() => this.checkForUpdate(false)))
        )
            .pipe(
                switchMap(() => interval(60 * 5000)), // continues normal checks every 5 minutes
                takeUntil(unrecoverable$)
            )
            .subscribe(() => {
                this.checkForUpdate();
            });
    }

    private checkForUpdate(requestUserVerification: boolean = true): void {
        this.swUpdate
            .checkForUpdate()
            .then((newVersion: boolean) => {
                console.log(`Checking for update, new version: ${newVersion}`); //JUST FOR DEBUGGING ON UAT, removel ater
                if (newVersion) {
                    if (requestUserVerification) {
                        this.requestUserToUpdate();
                    } else {
                        this.applyUpdate();
                    }
                }
            })
            .catch(error => {
                console.log(`Service worker check for update error: ${error}`);
            });
    }

    private applyUpdate(): void {
        this.swUpdate.activateUpdate().then(() => document.location.reload());
    }

    private requestUserToUpdate(): void {
        if (!this._dialogRef) {
            this._dialogRef = this.matDialog.open(
                SystemVersionUpdateDialogComponent,
                {
                    backdropClass: 'backdrop',
                    autoFocus: false,
                    disableClose: true,
                    restoreFocus: false
                }
            );

            this._dialogRef.componentInstance.onClickEvent
                .pipe(takeUntil(this._onDestroy$))
                .subscribe(() => this.applyUpdate());

            this._dialogRef.componentInstance.onCancelEvent
                .pipe(takeUntil(this._onDestroy$))
                .subscribe(() => {
                    this._dialogRef.close();
                    this._dialogRef = undefined;
                    this._onDestroy$.next();
                });
        }
    }
}
